import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import SiteTopNav from '@/components/SiteTopNav';
import ProductGallery from './ProductGallery';
import ProductAvailability from './ProductAvailability';
import ProductCertifications from './ProductCertifications';
import ProductSupport from './ProductSupport';
import styles from './index.module.scss';
import {
  useProductStore,
  toRouteKey,
  firstGalleryImageUrl,
  mergeProductGalleriesForDetail,
} from '@/store/product';
import img_pages_products_detail_related_expand_arrow_up from '@/assets/images/pages-products-detail-related-expand-arrow-up.png';

const RELATED_EXPAND_ARROW_UP = img_pages_products_detail_related_expand_arrow_up;

// Rendered instead as dedicated certification cards below the table (see
// <ProductCertifications />), not as plain attribute rows.
const ATTRIBUTE_LABELS_HIDDEN_FROM_TABLE = new Set([
  'TSCA EPA Title VI Certified',
  'CARB Phase 2 Compliant',
]);

/** Aligns with `.related-grid` columns in `index.module.scss` (4 / 2 / 1). */
const MQ_WIDE = '(min-width: 1024px)';
const MQ_TABLET = '(min-width: 768px)';

function getRelatedLayoutMode() {
  if (typeof window === 'undefined') return 'narrow';
  if (window.matchMedia(MQ_WIDE).matches) return 'wide';
  if (window.matchMedia(MQ_TABLET).matches) return 'tablet';
  return 'narrow';
}

function relatedFirstRowCount(mode) {
  if (mode === 'wide') return 4;
  return null;
}

function useRelatedLayoutMode() {
  const subscribe = useCallback(callback => {
    const mqWide = window.matchMedia(MQ_WIDE);
    const mqTablet = window.matchMedia(MQ_TABLET);
    mqWide.addEventListener('change', callback);
    mqTablet.addEventListener('change', callback);
    return () => {
      mqWide.removeEventListener('change', callback);
      mqTablet.removeEventListener('change', callback);
    };
  }, []);

  const getSnapshot = useCallback(() => getRelatedLayoutMode(), []);
  const getServerSnapshot = useCallback(() => 'narrow', []);

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

function mapGalleryItemsForView(galleryItems, productName) {
  if (!galleryItems?.length) return [];
  return galleryItems.map((item, i) => ({
    type: item.type,
    src: item.url ?? item.src,
    poster: item.poster,
    alt:
      item.alt ??
      (item.type === 'video' ? `${productName} product video` : `${productName} — view ${i + 1}`),
  }));
}

const Detail = () => {
  const { collectionKey, productKey } = useParams();
  const relatedLayoutMode = useRelatedLayoutMode();
  const [relatedExpanded, setRelatedExpanded] = useState(false);
  const collection = useProductStore(s =>
    s.collections.find(
      c => c.routeKey === collectionKey || toRouteKey(c.collection_name) === collectionKey
    )
  );
  const product = collection?.products?.find(
    p => p.routeKey === productKey || toRouteKey(p.product_name) === productKey
  );

  useEffect(() => {
    setRelatedExpanded(false);
  }, [relatedLayoutMode]);

  useEffect(() => {
    setRelatedExpanded(false);
  }, [productKey]);

  if (!collection) {
    return <Navigate to="/products" replace />;
  }

  const pathSegment = collection.routeKey ?? collectionKey;

  if (!product) {
    return <Navigate to={`/products/${pathSegment}`} replace />;
  }

  const collectionLabel = collection.collection_name ?? collection.title ?? '';
  const tableAttributes = (product.product_attributes ?? []).filter(
    ([label]) => !ATTRIBUTE_LABELS_HIDDEN_FROM_TABLE.has(label)
  );
  const galleryItems = mapGalleryItemsForView(
    mergeProductGalleriesForDetail(product),
    product.product_name
  );
  const relatedProducts = (collection.products ?? []).filter(p => p.routeKey !== product.routeKey);
  const relatedRowCount = relatedFirstRowCount(relatedLayoutMode);
  const showRelatedExpandToggle =
    relatedRowCount != null && relatedProducts.length > relatedRowCount;
  const visibleRelatedProducts =
    showRelatedExpandToggle && !relatedExpanded
      ? relatedProducts.slice(0, relatedRowCount)
      : relatedProducts;

  return (
    <div className={styles['detail-page']}>
      <div className={styles.page}>
        <header className={styles['detail-header']}>
          <SiteTopNav variant="light" />
        </header>

        <main className={styles['detail-main']}>
          <section className={styles['product-overview']} data-motion="section">
            <div className={styles['product-overview__gallery']} data-motion="card-soft">
              {galleryItems.length > 0 ? <ProductGallery items={galleryItems} /> : null}
            </div>

            <article className={styles['product-overview__content']} data-motion="card">
              <p className={styles['product-overview__collection']} data-motion="copy">
                {collectionLabel}
              </p>
              <h1 className={styles['product-overview__title']} data-motion="hero">
                {product.product_name}
              </h1>
              {product.sku ? (
                <p className={styles['product-overview__sku']} data-motion="copy">
                  {product.sku}
                </p>
              ) : null}
              {product.product_desc ? (
                <p className={styles['product-overview__description']} data-motion="copy">
                  {product.product_desc}
                </p>
              ) : null}

              {product.availabilityStatus ? (
                <ProductAvailability
                  status={product.availabilityStatus}
                  leadTime={product.estimatedLeadTime}
                  description={product.availabilityDescription}
                  href={product.availabilityLink}
                />
              ) : null}

              {!product.hideStandardCtas ? (
                <div className={styles['product-overview__actions']}>
                  <Link
                    to="/contact-us"
                    className={styles['button-primary']}
                    data-motion-interactive="press"
                  >
                    Order Sample
                  </Link>
                  <Link
                    to="/contact-us"
                    className={styles['button-outline']}
                    data-motion-interactive="press"
                  >
                    Where to Buy
                  </Link>
                </div>
              ) : null}
            </article>
          </section>

          {tableAttributes.length > 0 ? (
            <section
              className={styles['attributes-section']}
              aria-labelledby="product-attributes-title"
              data-motion="section"
              data-motion-threshold="0"
              data-motion-root-margin="0px 0px 0px 0px"
            >
              <h2
                id="product-attributes-title"
                className={styles['section-title']}
              >
                Product Attributes
              </h2>
              <dl className={styles['attributes-list']}>
                {tableAttributes.map(([label, value]) => (
                  <div key={label} className={styles['attributes-row']}>
                    <dt>{label}</dt>
                    <dd>{value || '-'}</dd>
                  </div>
                ))}
              </dl>
            </section>
          ) : null}

          <ProductCertifications />

          <ProductSupport />

          {relatedProducts.length > 0 ? (
            <section
              className={styles['related-section']}
              aria-labelledby="related-products-title"
              data-motion="section"
            >
              <h2
                id="related-products-title"
                className={styles['section-title']}
                data-motion="title"
              >
                View Others In This Collection
              </h2>
              <div id="related-products-grid" className={styles['related-grid']} aria-live="polite">
                {visibleRelatedProducts.map(p => (
                  <article key={p.routeKey} className={styles['related-card']} data-motion="card">
                    <img
                      src={firstGalleryImageUrl(p)}
                      alt={p.product_name}
                      referrerPolicy="no-referrer"
                    />
                    <h3>{p.product_name}</h3>
                    <Link
                      to={`/products/${pathSegment}/${p.routeKey}`}
                      className={styles['related-cardLink']}
                      data-motion-interactive="press"
                    >
                      Details
                    </Link>
                  </article>
                ))}
              </div>
              {showRelatedExpandToggle ? (
                <button
                  type="button"
                  className={styles['related-expand-toggle']}
                  aria-expanded={relatedExpanded}
                  aria-controls="related-products-grid"
                  onClick={() => setRelatedExpanded(v => !v)}
                  data-motion-interactive="press"
                >
                  <span className={styles['related-expand-toggle__inner']}>
                    <img
                      src={RELATED_EXPAND_ARROW_UP}
                      alt=""
                      width={24}
                      height={24}
                      className={
                        relatedExpanded
                          ? styles['related-expand-toggle__icon']
                          : `${styles['related-expand-toggle__icon']} ${styles['related-expand-toggle__icon--down']}`
                      }
                      referrerPolicy="no-referrer"
                    />
                    <span className={styles['related-expand-toggle__label']}>
                      {relatedExpanded ? 'SHOW LESS' : 'EXPAND TO VIEW ALL'}
                    </span>
                  </span>
                </button>
              ) : null}
            </section>
          ) : null}
        </main>
      </div>
    </div>
  );
};

export default Detail;
